import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  IconButton,
  Alert,
  Link,
  Stack,
  Collapse,
  Paper,
  Grid,
  useTheme,
  Snackbar,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  SmartToy as AIIcon,
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  Reply as ReplyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { UserContext } from "../../context/UserProvider";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";
import LeadsNotificationToggle from "./LeadsNotificationToggle";

const WhatsAppSettings = () => {
  const theme = useTheme();
  const { user: userData, loading } = useContext(UserContext);
  const [activeMenu, setActiveMenu] = useState("email");
  const [expandedMenus, setExpandedMenus] = useState(["ai"]);
  const [emailNotifications, setEmailNotifications] = useState([""]);
  const [phoneNotifications, setPhoneNotifications] = useState([""]);
  const [aiSubscriptions, setAiSubscriptions] = useState({
    leadsNotification: false,
    talkToLeads: false,
    autoReply: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Load phone numbers from user data
  useEffect(() => {
    if (
      userData?.leadNotifications &&
      Array.isArray(userData.leadNotifications)
    ) {
      if (userData.leadNotifications.length > 0) {
        setPhoneNotifications(userData.leadNotifications);
      } else {
        setPhoneNotifications([""]);
      }
    }
  }, [userData]);

  // Load emails from user data
  useEffect(() => {
    if (userData?.leadEmails && Array.isArray(userData.leadEmails)) {
      if (userData.leadEmails.length > 0) {
        setEmailNotifications(userData.leadEmails);
      } else {
        setEmailNotifications([""]);
      }
    }
  }, [userData]);

  const menus = [
    {
      id: "email",
      label: "Email Notification",
      icon: EmailIcon,
    },
    {
      id: "phone",
      label: "Phone Notification",
      icon: PhoneIcon,
    },
    {
      id: "ai",
      label: "AI Subscriptions",
      icon: AIIcon,
      submenus: [
        {
          id: "leads",
          label: "Leads Notification",
          icon: NotificationsIcon,
        },
        {
          id: "talk",
          label: "Talk to Leads",
          icon: ChatIcon,
        },
        {
          id: "auto",
          label: "Auto Reply",
          icon: ReplyIcon,
        },
      ],
    },
  ];

  const handleMenuToggle = (menuId) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  const handleEmailAdd = () => {
    if (emailNotifications.length < 5)
      setEmailNotifications([...emailNotifications, ""]);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emailNotifications];
    newEmails[index] = value;
    setEmailNotifications(newEmails);
  };

  const handleEmailRemove = (index) => {
    if (emailNotifications.length > 1) {
      setEmailNotifications(emailNotifications.filter((_, i) => i !== index));
    }
  };

  const handlePhoneAdd = () => {
    if (phoneNotifications.length < 5)
      setPhoneNotifications([...phoneNotifications, ""]);
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...phoneNotifications];
    newPhones[index] = value;
    setPhoneNotifications(newPhones);
  };

  const handlePhoneRemove = (index) => {
    if (phoneNotifications.length > 1) {
      setPhoneNotifications(phoneNotifications.filter((_, i) => i !== index));
    }
  };

  const handleAiToggle = (subscription) => {
    setAiSubscriptions((prev) => ({
      ...prev,
      [subscription]: !prev[subscription],
    }));
  };

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeMenu === "email") {
      // Filter out empty emails
      const validEmails = emailNotifications.filter((email) => email.trim());

      if (validEmails.length === 0) {
        setSnackbar({
          open: true,
          message: "Please add at least one email address",
          severity: "warning",
        });
        return;
      }

      // Validate email formats
      const invalidEmails = validEmails.filter((email) => !isValidEmail(email));
      if (invalidEmails.length > 0) {
        setSnackbar({
          open: true,
          message: "Please enter valid email addresses",
          severity: "error",
        });
        return;
      }

      setSubmitting(true);

      try {
        const response = await fetch(
          `${API_ENDPOINT}/user/assign-leads-emails`,
          {
            method: "POST",
            ...getHeader(),
            body: JSON.stringify({
              emails: validEmails,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update email addresses");
        }

        const data = await response.json();

        setSnackbar({
          open: true,
          message: "Email addresses updated successfully!",
          severity: "success",
        });
      } catch (error) {
        console.error("Error updating email addresses:", error);
        setSnackbar({
          open: true,
          message: "Failed to update email addresses. Please try again.",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    } else if (activeMenu === "phone") {
      // Filter out empty phone numbers
      const validPhones = phoneNotifications.filter((phone) => phone.trim());

      if (validPhones.length === 0) {
        setSnackbar({
          open: true,
          message: "Please add at least one phone number",
          severity: "warning",
        });
        return;
      }

      setSubmitting(true);

      try {
        const response = await fetch(
          `${API_ENDPOINT}/user/assign-leads-phones`,
          {
            method: "POST",
            ...getHeader(),
            body: JSON.stringify({
              phones: validPhones,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update phone numbers");
        }

        const data = await response.json();

        setSnackbar({
          open: true,
          message: "Phone numbers updated successfully!",
          severity: "success",
        });
      } catch (error) {
        console.error("Error updating phone numbers:", error);
        setSnackbar({
          open: true,
          message: "Failed to update phone numbers. Please try again.",
          severity: "error",
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderContent = () => {
    switch (activeMenu) {
      case "email":
        return (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Email Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage email addresses for lead notifications
            </Typography>
            {emailNotifications.map((email, index) => (
              <Stack
                key={index}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="Enter email address (e.g., user@example.com)"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  error={email.trim() && !isValidEmail(email)}
                  helperText={
                    email.trim() && !isValidEmail(email)
                      ? "Invalid email format"
                      : ""
                  }
                />
                {emailNotifications.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handleEmailRemove(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}

            {emailNotifications.length < 5 ? (
              <Button
                onClick={handleEmailAdd}
                startIcon={<AddIcon />}
                size="small"
              >
                Add Email
              </Button>
            ) : (
              <Alert severity="info">
                Maximum 5 emails.{" "}
                <Link href="/home/help-feedback">Contact Admin</Link>
              </Alert>
            )}
          </Stack>
        );

      case "phone":
        return (
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight="bold">
              Phone Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage phone numbers for lead notifications
            </Typography>
            {phoneNotifications.map((phone, index) => (
              <Stack
                key={index}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <TextField
                  fullWidth
                  size="small"
                  type="tel"
                  placeholder="Enter phone number (e.g., +1234567890)"
                  value={phone}
                  onChange={(e) => handlePhoneChange(index, e.target.value)}
                />
                {phoneNotifications.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handlePhoneRemove(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}

            {phoneNotifications.length < 5 ? (
              <Button
                onClick={handlePhoneAdd}
                startIcon={<AddIcon />}
                size="small"
              >
                Add Number
              </Button>
            ) : (
              <Alert severity="info">
                Maximum 5 numbers.{" "}
                <Link href="/home/help-feedback">Contact Admin</Link>
              </Alert>
            )}
          </Stack>
        );

      case "leads":
      case "talk":
      case "auto":
        const subMap = {
          leads: "leadsNotification",
          talk: "talkToLeads",
          auto: "autoReply",
        };

        const subId = subMap[activeMenu];
        const isActive = aiSubscriptions[subId];

        if (activeMenu === "leads") {
          return <LeadsNotificationToggle />;
        }

        // default card for other sub menus
        return (
          <Card variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Enable {activeMenu}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={() => handleAiToggle(subId)}
                      color="success"
                    />
                  }
                  label={isActive ? "Active" : "Inactive"}
                />
              </Stack>
            </CardContent>
          </Card>
        );

      default:
        return (
          <Box textAlign="center" py={4}>
            <SettingsIcon sx={{ fontSize: 48, color: "text.secondary" }} />
            <Typography color="text.secondary">
              Select a setting to configure
            </Typography>
          </Box>
        );
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Grid container>
          {/* Sidebar */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{ borderRight: 1, borderColor: "divider" }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                WhatsApp Settings
              </Typography>
              <List dense>
                {menus.map((menu) => (
                  <Box key={menu.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() =>
                          menu.submenus
                            ? handleMenuToggle(menu.id)
                            : setActiveMenu(menu.id)
                        }
                        selected={
                          activeMenu === menu.id ||
                          menu.submenus?.some((sub) => sub.id === activeMenu)
                        }
                        sx={{
                          borderRadius: 1,
                          "&.Mui-selected": {
                            bgcolor: theme.palette.action.selected,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <menu.icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={menu.label} />
                        {menu.submenus &&
                          (expandedMenus.includes(menu.id) ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          ))}
                      </ListItemButton>
                    </ListItem>

                    {menu.submenus && (
                      <Collapse in={expandedMenus.includes(menu.id)}>
                        <List dense disablePadding sx={{ pl: 3 }}>
                          {menu.submenus.map((sub) => (
                            <ListItem key={sub.id} disablePadding>
                              <ListItemButton
                                onClick={() => setActiveMenu(sub.id)}
                                selected={activeMenu === sub.id}
                                sx={{
                                  borderRadius: 1,
                                  "&.Mui-selected": {
                                    bgcolor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                  <sub.icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={sub.label}
                                  primaryTypographyProps={{
                                    fontSize: "0.85rem",
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Main */}
          <Grid item xs={12} md={7}>
            <Box sx={{ p: 3 }}>
              <form onSubmit={handleSubmit}>
                {renderContent()}

                {(activeMenu === "email" || activeMenu === "phone") && (
                  <Box
                    sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<CheckIcon />}
                      disabled={submitting}
                      sx={{ mt: 2 }}
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </Box>
                )}
              </form>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WhatsAppSettings;
